<template>
  <v-container grid-list-xs fluid class="k-pagination-footer" align-center>
    <v-layout row wrap :style="{ alignItems: 'center' }">
      <!-- rows per page dropdown -->
      <v-flex xs4>
        <span>Rows Per Page:</span>
        <pagination-options
          ref="pagination-options"
          :total-items-count="totalItemsCount"
          :filtered-items-count="filteredItemsCount"
          :is-server-side="isServerSide"
          :disabled="disabled"
          @set-page-size="setPageSize"
        />
        <span v-if="selectedItemsCount > 0" class="ml-1"
          ><em
            >{{ selectedItemsCount }} Row<span v-if="selectedItemsCount > 1"
              >s</span
            >
            Selected</em
          ></span
        >
      </v-flex>

      <!-- page navigation controls -->
      <v-flex xs4 class="k-page-nav-ctrls">
        <button
          class="k-page-btn"
          :disabled="isOnFirstPage"
          @mousedown="handleFirstPageNavClick"
        >
          «
        </button>
        <button
          class="k-page-btn"
          :disabled="isOnFirstPage"
          @mousedown="handlePrevPageNavClick"
        >
          ‹
        </button>
        <span class="k-page-current">{{ currPage }}</span>
        <button
          class="k-page-btn"
          :disabled="isOnLastPage"
          @mousedown="handleNextPageNavClick"
        >
          ›
        </button>
        <button
          class="k-page-btn"
          :disabled="isOnLastPage"
          @mousedown="handleLastPageNavClick"
        >
          »
        </button>
      </v-flex>

      <!-- items count, page and total -->
      <v-flex xs4 class="text-xs-right">
        {{ showingAmount }}
        <span v-if="hasFilters">(filtered from {{ totalItemsCount }})</span>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import PaginationOptions from './PaginationOptions.vue';

/**
 * - Close the dropdown menu when the user resizes the browser
 * - window.resize events are expensive, we want to add/remove as needed
 */

export default {
  name: 'PaginationControls',
  components: {
    PaginationOptions,
  },
  props: {
    skip: {
      type: Number,
      required: true,
    },
    filteredItemsCount: {
      type: Number,
      required: true,
    },
    totalItemsCount: {
      type: Number,
      required: true,
    },
    hasFilters: {
      type: Boolean,
      required: true,
    },
    // display the 'all' option for client-side tables only
    isServerSide: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedItemsCount: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      showDropdown: false,
      rowsPerPage: 10, // default 10
      currPage: 1,
    };
  },
  computed: {
    isOnFirstPage() {
      return this.rowsPerPage === 'all' || this.currPage === 1 || this.disabled;
    },
    isOnLastPage() {
      return (
        this.rowsPerPage === 'all' ||
        this.filteredItemsCount === 0 ||
        Math.ceil(this.filteredItemsCount / this.rowsPerPage) ===
          this.currPage ||
        this.disabled
      );
    },
    /**
     * @returns {String} e.g. 'Showing 1-10 of 25' | 'Showing 0'
     */
    showingAmount() {
      // if there are no filtered items
      if (this.filteredItemsCount === 0) return 'Showing 0';
      // calculate the theoretical index of the last row in the page
      const lastRowIndex =
        this.skip +
        (this.rowsPerPage === 'all'
          ? this.filteredItemsCount
          : this.rowsPerPage);
      // if the lastRowIndex exceeds the filtered items count, show that instead
      const endingRowIndex =
        lastRowIndex > this.filteredItemsCount
          ? this.filteredItemsCount
          : lastRowIndex;
      // return the string
      return `Showing ${this.skip + 1}-${endingRowIndex} of ${
        this.filteredItemsCount
      }`;
    },
  },
  methods: {
    /**
     * Callback from PaginationOptions
     * @param {Number|String} pageSize 5, 10, 25... | 'all'
     * @param {Boolean} emitCallback should we update the parent component
     */
    setPageSize(pageSize, emitCallback = true, isAllApplied = false) {
      // update the local variable
      this.rowsPerPage = pageSize;
      // propagate upward: emit pagination update event
      if (emitCallback) {
        this.$emit(
          'set-page-size',
          pageSize === 'all' ? this.totalItemsCount : pageSize,
          pageSize === 'all'
        );
        // propagate downward: update the child component
      } else {
        pageSize = isAllApplied ? 'all' : pageSize;
        this.$refs['pagination-options'].setPageSizeOption(pageSize);
      }
    },
    /**
     * Navigate to the first page
     */
    handleFirstPageNavClick() {
      // update the local current page variable
      this.currPage = 1;
      // emit the callback
      this.$emit('set-page', 1, false, true);
    },
    /**
     * Navigate to the previous page
     */
    handlePrevPageNavClick() {
      // subtract 1 form the current page number
      const currPage = this.currPage - 1;
      // set the curr page property
      this.currPage = currPage;
      // emit the callback to slice up the array of items
      this.$emit('set-page', currPage, true);
    },
    /**
     * Navigate to the next page
     */
    handleNextPageNavClick() {
      // add 1 to the current page number
      const currPage = this.currPage + 1;
      // update the curr page state
      this.currPage = currPage;
      // emit the callback
      this.$emit('set-page', currPage);
    },
    /**
     * Navigate to the last page
     */
    handleLastPageNavClick() {
      // get the max page number
      const lastPage = Math.ceil(this.filteredItemsCount / this.rowsPerPage);
      // set the current page
      this.currPage = lastPage;
      // emit the callback
      this.$emit('set-page', lastPage, false, true);
    },
    /**
     * Called externally from parent
     * @param {Number} page
     */
    setPage(page) {
      this.currPage = page;
    },
  },
};
</script>

<style lang="scss">
.k-pagination-footer {
  background: var(--sb-table-pagination-bg, #fafbfd);
  border-top: 1px solid var(--sb-table-outer-border, #e2e8f0);
  padding: 4px 10px;
  font-size: 12px;
  color: var(--sb-table-pagination-text, #94a3b8);
  /* "Rows Per Page:" and "Showing X of Y" labels */
  > .layout > .flex > span,
  > .layout > .flex.text-xs-right {
    color: var(--sb-table-pagination-text, #94a3b8);
  }
}
.k-page-nav-ctrls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.k-page-btn,
.k-page-current {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  margin: 0;
  border-radius: var(--sb-radius-sm, 6px);
  border: 1px solid #e2e8f0;
  background: var(--sb-table-body-bg, #fff);
  color: var(--sb-table-pagination-text, #64748b);
  font-family: inherit;
  font-size: 15px;
  line-height: 1;
  text-align: center;
  vertical-align: middle;
}
.k-page-btn {
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: var(--border, #cbd5e1);
    color: var(--sb-table-body-text, #1e293b);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}
.k-page-current {
  width: auto;
  min-width: 26px;
  padding: 0 6px;
  font-size: 12px;
  color: var(--sb-table-body-text, #1e293b) !important;
  border: 1px solid #e2e8f0;
}
html.dark .k-page-btn,
html.dark .k-page-current {
  border-color: #374151;
}
@media (prefers-color-scheme: dark) {
  html:not(.light):not(.dark) .k-page-btn,
  html:not(.light):not(.dark) .k-page-current {
    border-color: #374151;
  }
}
</style>
